'use client';

/**
 * Obeya Widget - Digital Situation Room Canvas
 *
 * @component_role RAG-annotated Project Visualization
 * @business_rule Two view modes:
 *   - Graph: Network relationships (Reagraph)
 *   - Kanban: Workflow stages (dnd-kit)
 * @user_outcome Toggle between "how things connect" and "where things are in workflow"
 * @integration_point klyde_edge /ki/obeya/project endpoint for RAG computation
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain obeya
 */

import { useState, useEffect } from 'react';
import { useMemo } from 'react';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  ChevronRight,
  Clock,
  ClipboardCheck,
  DollarSign,
  LayoutDashboard,
  LayoutGrid,
  Lightbulb,
  Loader2,
  Network,
  Columns,
  Pin,
  RefreshCw,
  FolderKanban,
  FileText,
  Package,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api/client';
import { ProjectGraphReagraph } from './project-graph-reagraph';
import { ObeyaKanban } from './obeya-kanban';
import { RFQComparisonWidget } from './rfq-comparison-widget';
import { TimelineView } from './rfi-timeline-view';

import { CostPlanDetailsPanel } from '../copilot/costplan-details-panel';
import { useObeyaProject, useObeyaProjects, useStrategyData, useViewportFast, useViewportSlow } from '@/lib/api/hooks';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useTotalMatrixStore } from '@/lib/stores/total-matrix-store';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { cn } from '@/lib/utils/cn';
import type { RAGNodeStatus, RFQCompareResponse, StrategyResult, HealthSynthesis, HealthRecommendation, VendorResponseSummary } from '@/lib/api/types';

type ViewMode = 'overview' | 'graph' | 'kanban' | 'matrix' | 'timeline' | 'costplan';

interface ObeyaWidgetProps {
  entityId?: string;
  title?: string;
  default_view?: string;
}

export function ObeyaWidget({ entityId, title, default_view }: ObeyaWidgetProps) {
  // default_view comes from matrix canvas_props — no hardcoded fallback
  const [viewMode, setViewMode] = useState<ViewMode>(
    (default_view as ViewMode) ?? 'overview'
  );

  // Server-driven labels (same React Query cache as explorer/overview)
  const listStrategy = useCapabilityStore((s) => s.strategyName('obeya', 'list'));
  const lang = useLanguageStore((s) => s.lang);
  const { data: labelsData } = useObeyaProjects(listStrategy!, { limit: 50, ...(lang ? { lang } : {}) });
  const L = labelsData?.labels ?? {};

  // Use entityId from props, or fall back to projectId from graph selection store
  const storeProjectId = useGraphSelectionStore((s) => s.projectId);
  const setProjectContext = useGraphSelectionStore((s) => s.setProjectContext);
  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);
  const activeProjectId = entityId ?? storeProjectId;

  // Track last-selected RFQ so Matrix/Timeline/Compose persist when clicking non-RFQ nodes
  const [contextRfqSid, setContextRfqSid] = useState<string | undefined>();
  const [contextRfqLabel, setContextRfqLabel] = useState<string | undefined>();
  // Track last-selected MCP so Cost Plan view persists when clicking non-MCP nodes
  const [contextMcpSid, setContextMcpSid] = useState<string | undefined>();

  useEffect(() => {
    if (selectedNode?.type === 'rfq') {
      setContextRfqSid(selectedNode.id);
      setContextRfqLabel(selectedNode.label);
    } else if (selectedNode?.type === 'mcp') {
      setContextMcpSid(selectedNode.id);
    }
  }, [selectedNode]);

  const rfqSid = selectedNode?.type === 'rfq' ? selectedNode.id : contextRfqSid;
  const rfqLabel = selectedNode?.type === 'rfq' ? selectedNode.label : contextRfqLabel;
  const mcpSid = selectedNode?.type === 'mcp' ? selectedNode.id : contextMcpSid;

  // default_view from matrix sets the INITIAL view on mount.
  // Selection changes within the same canvas don't reset the user's chosen view.

  // Pipeline-tile deep-link: when the home dashboard fires
  // setPipelineStageFocus(state) + switches mode to obeya, force
  // the kanban view so the column scroll/highlight has somewhere
  // to land. ObeyaKanban consumes (and clears) the focus signal.
  const pipelineStageFocus = useLayoutStore((s) => s.pipelineStageFocus);
  useEffect(() => {
    if (pipelineStageFocus) setViewMode('kanban');
  }, [pipelineStageFocus]);

  // When a simulation is selected, fetch it to get project info
  useEffect(() => {
    if (selectedNode?.type === 'simulation' && selectedNode.id && !storeProjectId) {
      // Fetch simulation detail to get project_sid
      import('@/lib/api/client').then(({ api }) => {
        api.getStrategyData<{ project_sid: string | null; project_name: string | null }>(
          'ki_simulation_detail', { simulation_id: selectedNode.id }
        ).then((res) => {
          if (res.success && res.data.project_sid) {
            setProjectContext(res.data.project_sid, {
              name: res.data.project_name ?? '',
              rag_status: 'unknown',
              rag_label: '',
              rfq_count: 0,
              po_count: 0,
            });
          }
        }).catch(console.error);
      });
    }
  }, [selectedNode?.id, selectedNode?.type, storeProjectId, setProjectContext]);

  // Canvas strategy from shared viewport selection hook.
  // Uses strategySelection (ignores graph clicks — clicking a node shouldn't change canvas view).
  // See viewport-selection.ts for the full priority chain.
  const { mode: activeMode, strategySelection } = useViewportSelection();
  const getViewportConfig = useTotalMatrixStore((s) => s.getViewportConfig);
  const viewportConfig = getViewportConfig(activeMode, strategySelection ?? 'project');
  const canvasStrategy = viewportConfig?.canvas?.strategy;
  const includeConversations = canvasStrategy === 'obeya_conversations';

  // Fetch real RAG data from klyde_edge
  const detailStrategy = useCapabilityStore((s) => s.strategyName('obeya', 'detail'));
  const { data, isLoading, error } = useObeyaProject(
    detailStrategy!,
    activeProjectId ? { project_id: activeProjectId, include_children: true } : null,
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-full flex items-center justify-center text-destructive">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm">{L.load_failed}</p>
        </div>
      </div>
    );
  }

  // Get RAG summary from API or default values
  const ragSummary = data?.summary ?? { red: 0, amber: 0, green: 0, unknown: 0 };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header Bar with Breadcrumbs */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border/50 bg-muted/30">
        <Breadcrumbs
          projectName={title || data?.project?.name}
          selectedNode={selectedNode}
        />

        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex items-center bg-muted/50 rounded-md p-0.5">
            <button
              onClick={() => setViewMode('overview')}
              className={cn(
                'flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors',
                viewMode === 'overview'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Activity className="w-3.5 h-3.5" />
              {L.overview}
            </button>
            <button
              onClick={() => setViewMode('matrix')}
              className={cn(
                'flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors',
                viewMode === 'matrix'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              {L.matrix}
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={cn(
                'flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors',
                viewMode === 'kanban'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Columns className="w-3.5 h-3.5" />
              {L.board}
            </button>
            <button
              onClick={() => setViewMode('costplan')}
              className={cn(
                'flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors',
                viewMode === 'costplan'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <DollarSign className="w-3.5 h-3.5" />
              {L.cost_plan}
            </button>
            <button
              onClick={() => setViewMode('timeline')}
              className={cn(
                'flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors',
                viewMode === 'timeline'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Clock className="w-3.5 h-3.5" />
              {L.timeline}
            </button>
            <button
              onClick={() => setViewMode('graph')}
              className={cn(
                'flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors',
                viewMode === 'graph'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Network className="w-3.5 h-3.5" />
              {L.graph}
            </button>
          </div>
        </div>
      </div>

      {/* Pipeline Funnel */}
      {data && <PipelineFunnel rfqs={data.rfqs} />}

      {/* Main Content Area */}
      <div className="flex-1 min-h-0 relative">
        {!activeProjectId ? (
          <PortfolioView onSelectProject={(sid, ctx) => setProjectContext(sid, ctx)} />
        ) : viewMode === 'overview' ? (
          <ObeyaOverview
            projectId={activeProjectId!}
            rfqs={data?.rfqs ?? []}
            pos={data?.pos ?? []}
            vendors={data?.vendors ?? []}
            ragSummary={ragSummary}
          />
        ) : viewMode === 'graph' ? (
          <>
            <ProjectGraphReagraph
              projectId={activeProjectId}
              projectName={title || data?.project?.name || undefined}
              depth={2}
              includeConversations={includeConversations}
            />

            {/* RAG Legend Overlay - only for graph view */}
            <div className="absolute bottom-4 left-4 bg-background/80 backdrop-blur-sm rounded-lg p-3 border border-border/50 text-xs">
              <div className="font-medium mb-2">{L.status_legend}</div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-3 h-3 text-red-500" />
                  <span>{L.overdue_blocked}</span>
                </div>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-3 h-3 text-amber-500" />
                  <span>{L.at_risk}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-3 h-3 text-green-500" />
                  <span>{L.on_track}</span>
                </div>
              </div>
            </div>
          </>
        ) : viewMode === 'matrix' ? (
          <RfiMatrixViewInline rfqSid={rfqSid} title={rfqLabel} />
        ) : viewMode === 'costplan' ? (
          <CostPlanDetailsPanel entityId={mcpSid ?? null} />
        ) : viewMode === 'timeline' ? (
          <TimelineView projectSid={activeProjectId!} selectedRfqSid={rfqSid} />
        ) : (
          <ObeyaKanban
            projectId={activeProjectId ?? ''}
            rfqs={data?.rfqs ?? []}
            pos={data?.pos ?? []}
          />
        )}

        {/* Metadata Overlay */}
        {data?.metadata && (
          <div className="absolute bottom-4 right-4 bg-background/80 backdrop-blur-sm rounded-lg px-2 py-1 border border-border/50 text-xs text-muted-foreground">
            {data.metadata.rules_applied} • {data.metadata.elapsed_ms}ms
          </div>
        )}
      </div>

      {/* Compose moved to copilot rfi_chat tab */}
    </div>
  );
}

function Breadcrumbs({
  projectName,
  selectedNode,
}: {
  projectName?: string;
  selectedNode: { id: string; label: string; type: string } | null;
}) {
  const activeMode = useLayoutStore((s) => s.activeExplorerView);
  const navItems = useTotalMatrixStore((s) => s.nav);
  const setProjectContext = useGraphSelectionStore((s) => s.setProjectContext);
  const clearSelection = useGraphSelectionStore((s) => s.clearSelection);

  const modeLabel = navItems.find((n) => n.mode === activeMode)?.label
    ?? activeMode.charAt(0).toUpperCase() + activeMode.slice(1);

  return (
    <div className="flex items-center gap-1 text-sm">
      <button
        onClick={() => { setProjectContext('', null as never); clearSelection(); }}
        className="text-muted-foreground hover:text-foreground transition-colors"
      >
        {modeLabel}
      </button>
      {projectName && (
        <>
          <ChevronRight className="w-3 h-3 text-muted-foreground/50" />
          <button
            onClick={() => clearSelection()}
            className={cn(
              'font-medium transition-colors truncate max-w-[200px]',
              selectedNode ? 'text-muted-foreground hover:text-foreground' : 'text-foreground'
            )}
          >
            {projectName}
          </button>
        </>
      )}
      {selectedNode && selectedNode.type !== 'project' && (
        <>
          <ChevronRight className="w-3 h-3 text-muted-foreground/50" />
          <span className="text-foreground font-medium truncate max-w-[200px]">
            {selectedNode.label}
          </span>
        </>
      )}
    </div>
  );
}

/** Thin wrapper around RFQComparisonWidget with BFF data fetch. */
function RfiMatrixViewInline({ rfqSid, title }: { rfqSid?: string; title?: string }) {
  const _ls = useCapabilityStore((s) => s.strategyName('obeya', 'list'));
  const _ln = useLanguageStore((s) => s.lang);
  const { data: _ld } = useObeyaProjects(_ls!, { limit: 50, ...(_ln ? { lang: _ln } : {}) });
  const L = _ld?.labels ?? {};
  const comparisonStrategy = useCapabilityStore((s) => s.strategyName('rfi', 'comparison'));
  const { data, isLoading } = useStrategyData<RFQCompareResponse>(
    comparisonStrategy ?? 'rfi_comparison',
    { rfq_sid: rfqSid ?? '' },
    !!rfqSid,
  );

  if (!rfqSid) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-muted-foreground">
        <div className="p-4 rounded-2xl bg-muted/30 mb-4">
          <LayoutGrid className="w-10 h-10 opacity-40" />
        </div>
        <p className="text-sm font-medium">{L.no_rfq_selected}</p>
        <p className="text-xs mt-1">{L.select_rfq_hint}</p>
      </div>
    );
  }

  const compareData = (data?.data ?? null) as RFQCompareResponse | null;

  return (
    <RFQComparisonWidget
      entityId={rfqSid}
      title={title}
      externalData={compareData}
      externalLoading={isLoading}
    />
  );
}

/** Procurement snapshot — progress summary for project overview.
 * Exported so the cards-everywhere mode (`mode1`) can reuse the same
 * rendering as the legacy obeya canvas — no duplicate JSX. */
export function ProcurementSnapshot({ rfqs, vendors }: { rfqs: RAGNodeStatus[]; vendors: RAGNodeStatus[] }) {
  const _listStrategy = useCapabilityStore((s) => s.strategyName('obeya', 'list'));
  const _lang = useLanguageStore((s) => s.lang);
  const { data: _labelsData } = useObeyaProjects(_listStrategy!, { limit: 50, ...(_lang ? { lang: _lang } : {}) });
  const L = _labelsData?.labels ?? {};
  const stats = useMemo(() => {
    const activeStates = new Set(['sent', 'rfq_sent']);
    const activeRfqs = rfqs.filter(r => activeStates.has(r.properties?.state as string));
    const rfqsSent = rfqs.filter(r => (r.properties?.state as string) === 'sent' || (r.properties?.state as string) === 'rfq_sent').length;
    const rfqsDraft = rfqs.filter(r => (r.properties?.state as string) === 'draft').length;

    // Collect vendor responses from active RFQs only
    const allResponses = activeRfqs.flatMap(r => r.vendor_responses ?? []);
    const respondedVendors = new Set(allResponses.map(vr => vr.vendor_name).filter(Boolean));
    const totals = allResponses
      .filter(vr => vr.total_amount && vr.total_amount > 0)
      .map(vr => vr.total_amount!);

    const minTotal = totals.length > 0 ? Math.min(...totals) : null;
    const maxTotal = totals.length > 0 ? Math.max(...totals) : null;

    // Deduplicate responses by vendor (keep latest / non-revised)
    const byVendor = new Map<string, VendorResponseSummary>();
    for (const vr of allResponses) {
      const key = vr.vendor_name || vr.semantic_id;
      const existing = byVendor.get(key);
      if (!existing || vr.rag_status !== 'revised') {
        byVendor.set(key, vr);
      }
    }

    return {
      rfqCount: rfqs.length,
      rfqsSent,
      rfqsDraft,
      vendorsInvited: vendors.length,
      vendorsResponded: respondedVendors.size,
      responseCount: allResponses.length,
      responses: [...byVendor.values()],
      minTotal,
      maxTotal,
    };
  }, [rfqs, vendors]);

  const fmt = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <LayoutDashboard className="w-3.5 h-3.5 text-violet-400" />
        <span className="text-[11px] font-semibold text-foreground uppercase tracking-wider">{L.procurement_snapshot}</span>
      </div>
      <div className="p-4 space-y-2.5">
        {/* RFQ Progress */}
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">{L.rfqs_issued}</span>
          <span className="text-[11px] font-medium">{stats.rfqsSent} sent{stats.rfqsDraft > 0 ? `, ${stats.rfqsDraft} draft` : ''}</span>
        </div>

        {/* Vendor Response Rate */}
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">{L.vendor_responses}</span>
          <span className="text-[11px] font-medium">
            {stats.vendorsResponded} of {stats.vendorsInvited} vendors
            {stats.vendorsInvited > 0 && (
              <span className={cn(
                'ml-1',
                stats.vendorsResponded === stats.vendorsInvited ? 'text-green-400' :
                stats.vendorsResponded === 0 ? 'text-muted-foreground' : 'text-amber-400'
              )}>
                ({Math.round((stats.vendorsResponded / stats.vendorsInvited) * 100)}%)
              </span>
            )}
          </span>
        </div>

        {/* Price Range */}
        {stats.minTotal !== null && stats.maxTotal !== null && (
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-muted-foreground">{L.quote_range}</span>
            <span className="text-[11px] font-medium">
              {stats.minTotal === stats.maxTotal
                ? fmt(stats.minTotal)
                : `${fmt(stats.minTotal)} — ${fmt(stats.maxTotal)}`
              }
              {stats.maxTotal > 0 && stats.minTotal < stats.maxTotal && (
                <span className="text-[10px] text-muted-foreground ml-1">
                  ({Math.round(((stats.maxTotal - stats.minTotal) / stats.maxTotal) * 100)}% {L.spread})
                </span>
              )}
            </span>
          </div>
        )}

        {/* Vendor response table */}
        {stats.responses.length > 0 && (
          <div className="pt-1 border-t border-border/50 space-y-1">
            {stats.responses.map((vr) => (
              <div key={vr.vendor_name} className="flex items-center justify-between">
                <span className="text-[11px] truncate max-w-40">{vr.vendor_name}</span>
                <span className="text-[11px] font-medium tabular-nums">
                  {vr.total_amount && vr.total_amount > 0 ? fmt(vr.total_amount) : '—'}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* No responses yet */}
        {stats.responseCount === 0 && stats.rfqsSent > 0 && (
          <div className="text-[10px] text-muted-foreground/60 italic pt-1">
            {L.awaiting_quotes}
          </div>
        )}
      </div>
    </div>
  );
}


/** Compact pipeline funnel — counts RFQ + PO states from Obeya data. */
function PipelineFunnel({ rfqs }: { rfqs: RAGNodeStatus[] }) {
  const _ls = useCapabilityStore((s) => s.strategyName('obeya', 'list'));
  const _ln = useLanguageStore((s) => s.lang);
  const { data: _ld } = useObeyaProjects(_ls!, { limit: 50, ...(_ln ? { lang: _ln } : {}) });
  const L = _ld?.labels ?? {};

  const PIPELINE_STAGES = [
    { key: 'draft',    label: L.draft,      color: 'text-muted-foreground', bg: 'bg-muted' },
    { key: 'sent',     label: L.sent,       color: 'text-blue-400',         bg: 'bg-blue-500/10' },
    { key: 'purchase', label: L.quoted,     color: 'text-amber-400',        bg: 'bg-amber-500/10' },
    { key: 'done',     label: L.rfq_closed, color: 'text-green-400',        bg: 'bg-green-500/10' },
  ];
  const counts = useMemo(() => {
    const c: Record<string, number> = { draft: 0, sent: 0, purchase: 0, done: 0 };
    for (const rfq of rfqs) {
      const state = (rfq.properties?.state as string) ?? 'draft';
      if (state in c) c[state]++;
      else c['draft']++;
    }
    return c;
  }, [rfqs]);

  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  if (total === 0) return null;

  return (
    <div className="px-4 py-1.5 border-b border-border/50 bg-muted/20 flex items-center gap-1">
      {PIPELINE_STAGES.map((stage, i) => (
        <div key={stage.key} className="flex items-center gap-1">
          <div className={cn('flex items-center gap-1.5 px-2 py-0.5 rounded', stage.bg)}>
            <span className={cn('text-sm font-semibold tabular-nums', stage.color)}>
              {counts[stage.key]}
            </span>
            <span className="text-[10px] text-muted-foreground">{stage.label}</span>
          </div>
          {i < PIPELINE_STAGES.length - 1 && (
            <ChevronRight className="w-3 h-3 text-muted-foreground/30" />
          )}
        </div>
      ))}
      {/* Proportional progress bar */}
      <div className="flex-1 flex h-1 rounded-full overflow-hidden gap-px ml-3">
        {PIPELINE_STAGES.map((stage) => (
          counts[stage.key] > 0 ? (
            <div
              key={stage.key}
              className={cn('h-full rounded-full', stage.bg)}
              style={{ flex: counts[stage.key] }}
            />
          ) : null
        ))}
      </div>
    </div>
  );
}

/** 4-quadrant overview — KPIs, Attention, Health Synthesis, Data Quality.
 *  Labels from server via useObeyaProjects (React Query deduplicates). */
function ObeyaOverview({
  projectId,
  rfqs,
  pos,
  vendors,
  ragSummary,
}: {
  projectId: string;
  rfqs: RAGNodeStatus[];
  pos: RAGNodeStatus[];
  vendors: RAGNodeStatus[];
  ragSummary: { red: number; amber: number; green: number; unknown: number };
}) {
  const activeMode = useLayoutStore((s) => s.activeExplorerView);
  const pinStrategy = useCapabilityStore((s) => s.strategyName('synthesis', 'pin'));
  const listStrategy = useCapabilityStore((s) => s.strategyName('obeya', 'list'));
  const lang = useLanguageStore((s) => s.lang);
  const { data: labelsData } = useObeyaProjects(listStrategy!, { limit: 50, ...(lang ? { lang } : {}) });
  const L = labelsData?.labels ?? {};
  const { data: fastData } = useViewportFast(activeMode, projectId, { lang });
  const {
    data: slowData,
    isLoading: isSynthesisLoading,
    refetch,
  } = useViewportSlow(activeMode, fastData?.context_key);

  const healthResult = slowData?.inspector?.project_health_check as StrategyResult | undefined;
  const healthSynthesis = healthResult?.decide_results?.health_synthesis as HealthSynthesis | undefined;
  const qualityResult = slowData?.inspector?.data_quality_check as StrategyResult | undefined;
  const qualitySynthesis = qualityResult?.decide_results?.data_quality_synthesis as HealthSynthesis | undefined;

  const attentionItems = useMemo(() =>
    [...rfqs, ...pos, ...vendors]
      .filter((item) => item.rag_status === 'red' || item.rag_status === 'amber')
      .sort((a, b) => (a.rag_status === 'red' ? 0 : 1) - (b.rag_status === 'red' ? 0 : 1)),
    [rfqs, pos, vendors]
  );


  const urgencyIcon = (u: string) => {
    if (u === 'low') return <CheckCircle className="h-4 w-4 text-green-400" />;
    if (u === 'high') return <AlertTriangle className="h-4 w-4 text-red-400" />;
    return <Clock className="h-4 w-4 text-amber-400" />;
  };

  const priorityColor = (p?: string) => {
    if (p === 'high') return 'bg-red-500/10 border-red-500/20 text-red-400';
    if (p === 'medium') return 'bg-amber-500/10 border-amber-500/20 text-amber-400';
    if (p === 'low') return 'bg-green-500/10 border-green-500/20 text-green-400';
    return 'bg-muted border-border';
  };

  // Pin synthesis to Odoo chatter timeline via BFF strategy
  const [isPinning, setIsPinning] = useState(false);
  const handlePinSynthesis = async (title: string, synthesis: HealthSynthesis | undefined, strategyName: string) => {
    if (!synthesis) return;
    setIsPinning(true);
    try {
      const pinResponse = await api.getStrategyData(pinStrategy!, {
        entity_id: projectId,
        title,
        summary: synthesis.summary,
        urgency: synthesis.urgency,
        recommendations: synthesis.recommendations || [],
        strategy_name: strategyName,
      });

      if (pinResponse.success && pinResponse.data?.success) {
        toast.success(L.pinned, {
          description: pinResponse.data.message_id
            ? `#${pinResponse.data.message_id} ${L.added_to_chatter}`
            : L.added_to_chatter,
        });
      } else {
        const errorMsg = pinResponse.data?.error || pinResponse.error || L.pin_failed;
        toast.error(L.pin_failed, { description: String(errorMsg) });
      }
    } catch (err) {
      toast.error(L.pin_failed, { description: err instanceof Error ? err.message : String(err) });
    } finally {
      setIsPinning(false);
    }
  };

  const renderSynthesisCard = (
    title: string,
    icon: React.ReactNode,
    synthesis: HealthSynthesis | undefined,
    strategyResult: StrategyResult | undefined,
    emptyLabel: string,
    onPin?: () => void,
  ) => (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-[11px] font-semibold text-foreground uppercase tracking-wider">{title}</span>
        </div>
        <div className="flex items-center gap-1">
          {onPin && (
            <button onClick={onPin} disabled={isPinning || !synthesis} className="p-1 rounded hover:bg-accent transition-colors disabled:opacity-50" title={L.pin_to_timeline}>
              <Pin className={cn('w-3.5 h-3.5 text-muted-foreground', isPinning && 'animate-pulse')} />
            </button>
          )}
          <button onClick={() => refetch()} disabled={isSynthesisLoading} className="p-1 rounded hover:bg-accent transition-colors disabled:opacity-50">
            <RefreshCw className={cn('w-3.5 h-3.5 text-muted-foreground', isSynthesisLoading && 'animate-spin')} />
          </button>
        </div>
      </div>
      <div className="p-4 overflow-y-auto">
        {isSynthesisLoading ? (
          <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
            <Loader2 className="h-5 w-5 mb-2 animate-spin" />
            <p className="text-[11px]">{L.generating}</p>
          </div>
        ) : !synthesis ? (
          <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
            <Activity className="h-5 w-5 mb-2 opacity-50" />
            <p className="text-[11px]">{emptyLabel}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Urgency card */}
            <div className={cn(
              'flex items-start gap-2 p-2.5 rounded-lg border',
              synthesis.urgency === 'low' && 'bg-green-500/10 border-green-500/20',
              synthesis.urgency === 'medium' && 'bg-amber-500/10 border-amber-500/20',
              synthesis.urgency === 'high' && 'bg-red-500/10 border-red-500/20',
            )}>
              {urgencyIcon(synthesis.urgency)}
              <div>
                <div className="text-[11px] font-medium capitalize">{synthesis.urgency} {L.urgency}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">{synthesis.summary}</div>
              </div>
            </div>
            {/* Recommendations with citations */}
            {synthesis.recommendations?.map((rec: HealthRecommendation, i: number) => (
              <div key={i} className={cn('p-2 rounded border', priorityColor(rec.priority))}>
                <div className="flex items-start gap-1.5">
                  <Lightbulb className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-medium">{rec.action}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1">
                      <ChevronRight className="h-2.5 w-2.5" />
                      {L.based_on} {rec.based_on}
                    </div>
                  </div>
                  {rec.priority && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-background/50 capitalize shrink-0">
                      {rec.priority}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {/* Trust level */}
            {strategyResult && (
              <div className="text-[10px] text-muted-foreground pt-2 border-t border-border/50 flex items-center gap-2">
                <span>{L.trust} {strategyResult.trust_level}</span>
                {(strategyResult.errors?.length ?? 0) > 0 && (
                  <span className="text-red-400">({strategyResult.errors?.length} {L.errors})</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="h-full overflow-y-auto p-4 space-y-4">
      {/* KPI Strip */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { id: 'rfqs', label: L.rfqs, value: rfqs.length, Icon: LayoutGrid, accent: 'text-blue-400' },
          { id: 'vendors', label: L.vendors, value: vendors.length, Icon: Activity, accent: 'text-violet-400' },
          { id: 'red', label: L.red, value: ragSummary.red, Icon: AlertCircle, accent: ragSummary.red > 0 ? 'text-red-400' : 'text-muted-foreground' },
          { id: 'amber', label: L.amber, value: ragSummary.amber, Icon: AlertTriangle, accent: ragSummary.amber > 0 ? 'text-amber-400' : 'text-muted-foreground' },
        ].map((kpi) => (
          <div key={kpi.id} className="bg-card rounded-xl border border-border p-3.5">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">{kpi.label}</span>
              <kpi.Icon className={cn('w-3.5 h-3.5', kpi.accent)} />
            </div>
            <p className="text-xl font-bold text-foreground tabular-nums">{kpi.value}</p>
          </div>
        ))}
      </div>

      {/* 4 Quadrants */}
      <div className="grid grid-cols-2 gap-4">
        {/* Q1: Needs Attention */}
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-[11px] font-semibold text-foreground uppercase tracking-wider">{L.needs_attention}</span>
              <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">{attentionItems.length}</span>
            </div>
          </div>
          <div className="divide-y divide-border/50 max-h-52 overflow-y-auto">
            {attentionItems.length === 0 ? (
              <div className="px-4 py-6 text-center text-[11px] text-muted-foreground/50 italic">{L.all_clear}</div>
            ) : attentionItems.slice(0, 6).map((item) => (
              <button
                key={item.semantic_id}
                onClick={() => {
                  const store = useGraphSelectionStore.getState();
                  store.selectNode({
                    id: item.semantic_id,
                    label: item.name || item.semantic_id,
                    type: item.entity_type.toLowerCase(),
                  });
                }}
                className={cn(
                  'w-full text-left flex items-start gap-2 px-4 py-2.5 border-l-2 hover:bg-accent/30 transition-colors cursor-pointer',
                  item.rag_status === 'red' ? 'border-l-red-500 bg-red-500/5' : 'border-l-amber-500 bg-amber-500/5'
                )}
              >
                {item.rag_status === 'red'
                  ? <AlertCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
                  : <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-medium text-foreground truncate">{item.name || item.semantic_id}</p>
                  <p className="text-[10px] text-muted-foreground">{item.entity_type} · {item.rag_label}</p>
                </div>
                <ChevronRight className="w-3 h-3 text-muted-foreground/40 mt-0.5 shrink-0" />
              </button>
            ))}
          </div>
        </div>

        {/* Q2: Health Synthesis (with pin-to-timeline) */}
        {renderSynthesisCard(
          L.project_health,
          <Activity className="w-3.5 h-3.5 text-blue-400" />,
          healthSynthesis,
          healthResult,
          L.no_synthesis,
          () => handlePinSynthesis(L.project_health, healthSynthesis, 'health_synthesis'),
        )}

        {/* Q3: Data Quality (with pin-to-timeline) */}
        {renderSynthesisCard(
          L.data_quality,
          <ClipboardCheck className="w-3.5 h-3.5 text-green-400" />,
          qualitySynthesis,
          qualityResult,
          L.no_quality,
          () => handlePinSynthesis(L.data_quality, qualitySynthesis, 'data_quality'),
        )}

        {/* Q4: Procurement Snapshot */}
        <ProcurementSnapshot rfqs={rfqs} vendors={vendors} />
      </div>
    </div>
  );
}

/** Portfolio view — shown when no project is selected. */
export function PortfolioView({ onSelectProject }: {
  onSelectProject: (sid: string, ctx: { name: string; rag_status: string; rag_label: string; rfq_count: number; po_count: number }) => void;
}) {
  const listStrategy = useCapabilityStore((s) => s.strategyName('obeya', 'list'));
  const lang = useLanguageStore((s) => s.lang);
  const { data, isLoading, error } = useObeyaProjects(listStrategy!, { limit: 50, ...(lang ? { lang } : {}) });
  const L = data?.labels ?? {};
  const projects = data?.projects ?? [];
  const summary = data?.summary;

  const kpis = useMemo(() => {
    let rfqs = 0, pos = 0, value = 0;
    for (const p of projects) { rfqs += p.rfq_count; pos += p.po_count; value += p.project_value ?? 0; }
    return { projects: projects.length, rfqs, pos, value };
  }, [projects]);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || projects.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <FolderKanban className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p className="text-sm">{error ? L.load_failed : L.no_projects}</p>
        </div>
      </div>
    );
  }

  const RAG_BORDER: Record<string, string> = {
    red: 'border-l-red-500',
    amber: 'border-l-amber-500',
    green: 'border-l-green-500',
    unknown: 'border-l-muted-foreground/30',
  };
  const RAG_DOT: Record<string, string> = {
    red: 'bg-red-500',
    amber: 'bg-amber-500',
    green: 'bg-green-500',
    unknown: 'bg-muted-foreground/40',
  };

  return (
    <div className="h-full overflow-y-auto p-4 space-y-4">
      {/* KPI Strip */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: L.projects, value: String(kpis.projects), Icon: FolderKanban, accent: 'text-blue-400' },
          { label: L.rfqs, value: String(kpis.rfqs), Icon: FileText, accent: 'text-yellow-400' },
          { label: L.portfolio_value, value: kpis.value > 0 ? `$${kpis.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—', Icon: DollarSign, accent: 'text-emerald-400' },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-card rounded-xl border border-border p-3.5">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">{kpi.label}</span>
              <kpi.Icon className={cn('w-3.5 h-3.5', kpi.accent)} />
            </div>
            <p className="text-xl font-bold text-foreground tabular-nums">{kpi.value}</p>
          </div>
        ))}
      </div>

      {/* RAG Summary */}
      {summary && (
        <div className="flex items-center gap-3 text-xs">
          {[
            { label: L.red, count: summary.red, color: 'text-red-500', dot: 'bg-red-500' },
            { label: L.amber, count: summary.amber, color: 'text-amber-500', dot: 'bg-amber-500' },
            { label: L.green, count: summary.green, color: 'text-green-500', dot: 'bg-green-500' },
          ].map((r) => (
            <div key={r.label} className="flex items-center gap-1.5">
              <span className={cn('w-2 h-2 rounded-full', r.dot)} />
              <span className={cn('font-medium', r.color)}>{r.count}</span>
              <span className="text-muted-foreground">{r.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Project Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {projects.map((project) => (
          <button
            key={project.semantic_id}
            onClick={() => onSelectProject(project.semantic_id, {
              name: project.name,
              rag_status: project.rag_status,
              rag_label: project.rag_label,
              rfq_count: project.rfq_count,
              po_count: project.po_count,
            })}
            className={cn(
              'group text-left bg-card rounded-xl border border-border border-l-2 p-4',
              RAG_BORDER[project.rag_status] ?? RAG_BORDER.unknown,
              'hover:bg-accent/50 hover:border-foreground/20 hover:shadow-md',
              'hover:-translate-y-0.5 transition-all cursor-pointer',
            )}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className={cn('w-2 h-2 rounded-full shrink-0', RAG_DOT[project.rag_status] ?? RAG_DOT.unknown)} />
                <span className="text-sm font-medium truncate">{project.name}</span>
              </div>
              <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />
            </div>
            <div className="flex items-center gap-2 mb-3">
              {project.customer_name && (
                <span className="text-[11px] text-muted-foreground truncate">{project.customer_name}</span>
              )}
              {project.rag_label && (
                <span className="text-[10px] text-muted-foreground/60 truncate ml-auto">{project.rag_label}</span>
              )}
            </div>
            {project.project_value != null && project.project_value > 0 && (
              <div className="flex items-center gap-1 mb-2 text-sm font-semibold text-emerald-400">
                <DollarSign className="w-3.5 h-3.5" />
                <span>{project.project_value.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              </div>
            )}
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <FileText className="w-3 h-3" />
                <span>{project.rfq_count} RFQ{project.rfq_count !== 1 ? 's' : ''}</span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/** State breakdown bar chart for RFQs or POs. */
function BreakdownSection({ label, icon, items, color }: { label: string; icon: React.ReactNode; items: RAGNodeStatus[]; color: string }) {
  const grouped = useMemo(() => {
    const m: Record<string, number> = {};
    for (const item of items) {
      const s = (item.properties?.state as string) ?? 'unknown';
      m[s] = (m[s] || 0) + 1;
    }
    return Object.entries(m);
  }, [items]);

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        {icon}
        <span className="text-[11px] font-medium text-foreground">{label}</span>
      </div>
      <div className="space-y-1">
        {grouped.map(([state, count]) => (
          <div key={state} className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground capitalize">{state}</span>
            <div className="flex items-center gap-2">
              <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                <div className={cn('h-full rounded-full', color)} style={{ width: `${(count / items.length) * 100}%` }} />
              </div>
              <span className="text-[10px] font-medium text-foreground tabular-nums w-4 text-right">{count}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
